// frontend/src/components/auth/ResetPasswordForm.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { authAPI } from '../../services/api';
import AuthLayout from './AuthLayout';

export const ResetPasswordForm = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setErrors({ general: 'Invalid or missing reset token. Please request a new password reset link.' });
    } else {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.newPassword) {
      newErrors.newPassword = 'Password is required';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.newPassword)) {
      newErrors.newPassword = 'Must contain uppercase, lowercase, and number';
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      setErrors({ general: 'Invalid reset token' });
      return;
    }
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    try {
      await authAPI.resetPassword(token, formData.newPassword);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      setErrors({
        general: error.message || 'Failed to reset password. The link may have expired.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: 'bg-border-default' };
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (password.length >= 12) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[^a-zA-Z\d]/.test(password)) strength += 1;

    const labels = ['Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];
    const colors = ['bg-danger', 'bg-warning', 'bg-warning', 'bg-success', 'bg-success'];
    return {
      strength,
      label: labels[strength - 1] || '',
      color: colors[strength - 1] || 'bg-border-default'
    };
  };

  const passwordStrength = getPasswordStrength(formData.newPassword);

  if (success) {
    return (
      <AuthLayout title="Password reset successful">
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-success-tint mb-4">
            <CheckCircle className="h-8 w-8 text-success" />
          </div>
          <p className="text-body text-ink-muted mb-2">
            Your password has been successfully reset.
          </p>
          <p className="text-small text-ink-muted">Redirecting to login...</p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="w-full min-h-touch mt-6 flex items-center justify-center rounded-lg
            bg-accent text-white font-medium text-body
            hover:bg-accent-hover transition-colors"
        >
          Go to login
        </button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset your password" subtitle="Enter your new password below.">
      <form className="space-y-5" onSubmit={handleSubmit}>
        {errors.general && (
          <div className="rounded-lg bg-danger-tint border border-danger-border p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-danger flex-shrink-0 mt-0.5" />
              <p className="text-body text-danger">{errors.general}</p>
            </div>
          </div>
        )}

        {/* New Password */}
        <div className="space-y-1.5">
          <label htmlFor="newPassword" className="block text-small font-semibold text-ink-soft">
            New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="newPassword"
              name="newPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={formData.newPassword}
              onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.newPassword ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Enter new password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-touch justify-center"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="h-5 w-5 text-ink-muted" /> : <Eye className="h-5 w-5 text-ink-muted" />}
            </button>
          </div>

          {/* Password Strength */}
          {formData.newPassword && (
            <div className="mt-2">
              <div className="flex items-center justify-between text-small mb-1">
                <span className="text-ink-muted">Strength:</span>
                <span className="font-medium text-ink-soft">{passwordStrength.label}</span>
              </div>
              <div className="w-full bg-surface-muted rounded-full h-2">
                <div
                  className={`${passwordStrength.color} h-2 rounded-full transition-all duration-300`}
                  style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                />
              </div>
            </div>
          )}

          {errors.newPassword && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.newPassword}
            </p>
          )}
          <p className="text-small text-ink-muted">
            At least 8 characters with uppercase, lowercase, and numbers.
          </p>
        </div>

        {/* Confirm Password */}
        <div className="space-y-1.5">
          <label htmlFor="confirmPassword" className="block text-small font-semibold text-ink-soft">
            Confirm New Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="h-5 w-5 text-ink-muted" />
            </div>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={formData.confirmPassword}
              onChange={handleChange}
              className={`w-full min-h-touch pl-10 pr-10 rounded-lg border text-body
                focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent
                bg-white text-ink-soft placeholder-ink-muted
                ${errors.confirmPassword ? 'border-danger' : 'border-border-strong'}`}
              placeholder="Confirm new password"
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center min-w-touch justify-center"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
            >
              {showConfirmPassword ? <EyeOff className="h-5 w-5 text-ink-muted" /> : <Eye className="h-5 w-5 text-ink-muted" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-small text-danger flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.confirmPassword}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={loading || !token}
          className="w-full min-h-touch flex items-center justify-center rounded-lg
            bg-accent text-white font-medium text-body
            hover:bg-accent-hover transition-colors
            disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            'Reset password'
          )}
        </button>

        <p className="text-center">
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="text-body text-ink-muted hover:text-ink-soft transition-colors"
          >
            Back to login
          </button>
        </p>
      </form>
    </AuthLayout>
  );
};
